import {AbstractPanel} from '../stats-panel';
import type {ITextureData, RenderingContext} from '../types';
import {generateFormatMap, generateTextureMap, generateTypeMap, patch} from '../utils';

const KB = 1024;
const MB = KB * KB;

export class GPUMemoryPanel extends AbstractPanel {
    private static _realTexImage2D: RenderingContext['texImage2D'];
    private static _compressedTexImage2D: RenderingContext['compressedTexImage2D'];
    private static _realCreateTexture: RenderingContext['createTexture'];
    private static _realDeleteTexture: RenderingContext['deleteTexture'];
    private static _realGenerateMipmap: RenderingContext['generateMipmap'];

    private static _textureMap: Record<GLenum, GLenum>;
    private static _typeMap: Record<GLenum, number>;
    private static _formatMap: Record<GLenum, number>;

    private _memory!: number;
    private _gl!: RenderingContext;
    private _textures!: Map<WebGLTexture, ITextureData>;

    public constructor(name = 'GPU', bg = '#201', fg = '#f08', max = 100, decimals = 0) {
        super(name, bg, fg, max, decimals);
    }

    public get value(): number {
        return this._memory / MB;
    }

    public update(): void {
        //
    }

    public onInstall(gl?: RenderingContext): void {
        if (!gl) {
            throw Error('Stats: GPUMemoryPanel > Rendering context must be provided');
        }

        this._gl = gl;
        this._memory = 0;
        this._textures = new Map();

        GPUMemoryPanel._typeMap = generateTypeMap(gl);
        GPUMemoryPanel._textureMap = generateTextureMap(gl);
        GPUMemoryPanel._formatMap = generateFormatMap(gl);

        GPUMemoryPanel._realTexImage2D = gl.texImage2D;
        GPUMemoryPanel._compressedTexImage2D = gl.compressedTexImage2D;
        GPUMemoryPanel._realCreateTexture = gl.createTexture;
        GPUMemoryPanel._realDeleteTexture = gl.deleteTexture;
        GPUMemoryPanel._realGenerateMipmap = gl.generateMipmap;

        gl.texImage2D = patch(gl.texImage2D, this._fakeTexImage2D.bind(this));
        gl.compressedTexImage2D = patch(gl.compressedTexImage2D, this._fakeCompressedTexImage2D.bind(this));
        gl.createTexture = patch(gl.createTexture, this._fakeCreateTexture.bind(this));
        gl.deleteTexture = patch(gl.deleteTexture, this._fakeDeleteTexture.bind(this));
        gl.generateMipmap = patch(gl.generateMipmap, this._fakeGenerateMipmap.bind(this));
    }

    public onUninstall(): void {
        this._gl.texImage2D = GPUMemoryPanel._realTexImage2D;
        this._gl.compressedTexImage2D = GPUMemoryPanel._compressedTexImage2D;
        this._gl.createTexture = GPUMemoryPanel._realCreateTexture;
        this._gl.deleteTexture = GPUMemoryPanel._realDeleteTexture;
        this._gl.generateMipmap = GPUMemoryPanel._realGenerateMipmap;
    }

    private _calculateTextureSize(format: GLenum, type: GLenum, width: GLsizei, height: GLsizei): number {
        const size = width * height;
        const formatSize = GPUMemoryPanel._formatMap[format];
        const typeSize = GPUMemoryPanel._typeMap[type];
        const byteSize = size * (formatSize * typeSize || 1);

        return byteSize;
    }

    private _calculateMemory(): number {
        let memory = 0;

        this._textures.forEach(({ size, mipmap }) => {
            memory += size * (mipmap ? 2 : 1);
        });

        return memory;
    }

    private _fakeTexImage2D(result: void, ...args: any[]): void {
        const glTexture: WebGLTexture = this._gl.getParameter(GPUMemoryPanel._textureMap[args[0]]);

        let format: GLenum = this._gl.RGBA;
        let type: GLenum = this._gl.FLOAT;
        let width: GLsizei = 0;
        let height: GLsizei = 0;

        if (args.length === 9) {
            /* WebGLRenderingContext */
            width = args[3];
            height = args[4];
            format = args[6];
            type = args[7];
        } else if (args.length === 6) {
            /* WebGL2RenderingContext */
            width = args[5].width;
            height = args[5].height;
            format = args[3];
            type = args[4];
        }

        const size = this._calculateTextureSize(format, type, width, height);

        const data = this._textures.get(glTexture)!;

        data.size = size;
        data.width = width;
        data.height = height;
        data.mipmap = false;

        this._memory = this._calculateMemory();
    }

    private _fakeCompressedTexImage2D(result: void, ...args: any[]): void {
        const glTexture: WebGLTexture = this._gl.getParameter(GPUMemoryPanel._textureMap[args[0]]);

        const data = this._textures.get(glTexture)!;

        const width: GLsizei = args[3] as GLsizei;
        const height: GLsizei = args[4] as GLsizei;
        const arrayBufferView = args[6] as ArrayBufferView;

        data.size = arrayBufferView.byteLength;
        data.width = width;
        data.height = height;
        data.mipmap = false;

        this._memory = this._calculateMemory();
    }

    private _fakeCreateTexture(result: WebGLTexture | null, ...args: any[]): void {
        if (!result) {
            return;
        }

        this._textures.set(result, { width: 0, height: 0, size: 0, mipmap: false });
    }

    private _fakeGenerateMipmap(result: void, ...args: any[]): void {
        const glTexture = args[0];
        const webGLTexture = this._gl.getParameter(GPUMemoryPanel._textureMap[glTexture]);
        const data = this._textures.get(webGLTexture)!;

        data.mipmap = true;

        this._memory = this._calculateMemory();
    }

    private _fakeDeleteTexture(result: void, ...args: any[]): void {
        const glTexture: WebGLTexture = args[0];

        this._textures.delete(glTexture);
        this._memory = this._calculateMemory();
    }
}
